import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useWhaleSummaries, getSentimentColor, getSentimentLabel, useTrackWhaleMovements } from "@/hooks/useWhaleTracking";
import { TrendingUp, TrendingDown, Activity, Wallet, RefreshCw, AlertTriangle } from "lucide-react";
import { useState } from "react";

export const SmartMoneyTracker = () => {
  const { data: summaries, isLoading, error, refetch } = useWhaleSummaries();
  const trackMutation = useTrackWhaleMovements();
  const [isTracking, setIsTracking] = useState(false);

  const handleRefresh = async () => {
    setIsTracking(true);
    try {
      await trackMutation.mutateAsync();
      refetch();
    } finally {
      setIsTracking(false);
    }
  };

  // Sort by sentiment strength
  const sortedSummaries = summaries?.slice().sort((a, b) => {
    const sentimentOrder: Record<string, number> = {
      'strong_accumulation': 5,
      'accumulation': 4,
      'neutral': 3,
      'distribution': 2,
      'strong_distribution': 1,
    };
    return (sentimentOrder[b.whale_sentiment || 'neutral'] || 3) - 
           (sentimentOrder[a.whale_sentiment || 'neutral'] || 3);
  });

  // Filter for strong signals
  const strongSignals = sortedSummaries?.filter(
    s => s.whale_sentiment === 'strong_accumulation' || s.whale_sentiment === 'strong_distribution'
  );

  const formatUsd = (value: number) => {
    if (Math.abs(value) >= 1000000) {
      return `A$${(value / 1000000).toFixed(2)}M`;
    }
    if (Math.abs(value) >= 1000) {
      return `A$${(value / 1000).toFixed(1)}K`;
    }
    return `A$${value.toFixed(0)}`;
  };

  if (isLoading) {
    return (
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            Smart Money Tracker
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 backdrop-blur border-border/50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Wallet className="h-5 w-5 text-primary" />
            Smart Money Tracker
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleRefresh}
            disabled={isTracking || trackMutation.isPending}
            className="h-8 px-2"
          >
            <RefreshCw className={`h-4 w-4 ${isTracking || trackMutation.isPending ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Whale wallet movements via on-chain data (EVM chains only)
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Strong Signals Alert */}
        {strongSignals && strongSignals.length > 0 && (
          <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Strong Signals Detected</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {strongSignals.slice(0, 5).map(signal => (
                <Badge 
                  key={signal.token_symbol}
                  variant={signal.whale_sentiment === 'strong_accumulation' ? 'default' : 'destructive'}
                  className="text-xs"
                >
                  {signal.token_symbol}
                  {signal.whale_sentiment === 'strong_accumulation' ? (
                    <TrendingUp className="h-3 w-3 ml-1" />
                  ) : (
                    <TrendingDown className="h-3 w-3 ml-1" />
                  )}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Token Movement List */}
        {sortedSummaries && sortedSummaries.length > 0 ? (
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {sortedSummaries.slice(0, 10).map(summary => (
              <div 
                key={summary.token_symbol}
                className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-sm font-bold">{summary.token_symbol.slice(0, 3)}</span>
                  </div>
                  <div>
                    <span className="font-medium">{summary.token_symbol}</span>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs ${getSentimentColor(summary.whale_sentiment as any)}`}>
                        {getSentimentLabel(summary.whale_sentiment as any)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right">
                  <div className={`text-sm font-medium ${
                    (summary.net_flow_usd || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {(summary.net_flow_usd || 0) >= 0 ? '+' : ''}
                    {formatUsd(summary.net_flow_usd || 0)}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground justify-end">
                    <TrendingUp className="h-3 w-3 text-green-400" />
                    <span>{summary.accumulation_count || 0}</span>
                    <TrendingDown className="h-3 w-3 text-red-400" />
                    <span>{summary.distribution_count || 0}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
            {error ? (
              <>
                <p className="text-sm text-destructive">Failed to load whale data</p>
                <p className="text-xs mt-1">Try refreshing — the tracker may be warming up</p>
              </>
            ) : (
              <>
                <p className="text-sm">No smart money movements detected</p>
                <p className="text-xs mt-1">Click refresh to fetch latest data</p>
              </>
            )}
          </div>
        )}

        {/* Footer Stats */}
        {sortedSummaries && sortedSummaries.length > 0 && (
          <div className="pt-2 border-t border-border/50 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Tracking {sortedSummaries.length} tokens
            </span>
            <span>
              {summaries?.[0]?.analyzed_at && 
                `Updated ${new Date(summaries[0].analyzed_at).toLocaleTimeString('en-AU', { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}`
              }
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SmartMoneyTracker;
